import React, { useState, useEffect, useCallback } from 'react';
import { Form, Button, Spinner, Row, Col } from 'react-bootstrap';
import {
  BsEnvelope, BsTrash, BsPencil, BsEye, BsSearch,
  BsXCircle, BsChatLeftText, BsTelephone, BsClock,
  BsCheckCircle, BsEnvelopeOpen, BsFilter,
  BsPaperclip, BsDownload, BsFileEarmark, BsFileEarmarkImage,
} from 'react-icons/bs';
import StarDivider from '../components/uiComponents/StarDivider';
import Toast from '../components/Toast';
import PortalModal from '../components/PortalModal';
import Pagination from '../components/Pagination';
import contactMessageService from '../api/contactMessageService';

/* ─── Constants ─────────────────────────────────────────────────────────────── */

const SUBJECTS = [
  { value: 'testimony', label: 'Témoignage' },
  { value: 'prayer_request', label: 'Demande de prière' },
  { value: 'information', label: 'Information' },
  { value: 'other', label: 'Autre' },
];

const STATUTS = [
  { value: 'nouveau', label: 'Nouveau (Non lu)', bg: '#fce8e8', color: '#8B0000' },
  { value: 'lu', label: 'Lu', bg: '#fff3cd', color: '#856404' },
  { value: 'repondu', label: 'Répondu', bg: '#d5f5e3', color: '#1e8449' },
  { value: 'archive', label: 'Archivé', bg: '#f0f0f0', color: '#666' },
];

function subjectLabel(v) {
  return SUBJECTS.find(s => s.value === v)?.label || v;
}
function statutInfo(v) {
  return STATUTS.find(s => s.value === v) || { label: v, bg: '#f0f0f0', color: '#666' };
}
function extractError(err) {
  if (err?.response?.data?.errors?.length)
    return err.response.data.errors.map(e => e.message).join(', ');
  return err?.response?.data?.message || err?.message || 'Une erreur est survenue';
}
function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}
function formatDateShort(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

/* ─── Stat card ─────────────────────────────────────────────────────────────── */
function StatCard({ icon: Icon, label, value, bg, color }) {
  return (
    <div style={{
      backgroundColor: '#fff', border: '1px solid #ece8e8', borderRadius: 12,
      padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, backgroundColor: bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={18} color={color} />
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a', lineHeight: 1 }}>{value ?? '—'}</div>
        <div className='notranslate' style={{ fontSize: 11, color: '#777', marginTop: 3 }}>{label}</div>
      </div>
    </div>
  );
}

/* ─── Status badge ───────────────────────────────────────────────────────────── */
function StatutBadge({ statut }) {
  const info = statutInfo(statut);
  return (
    <span style={{
      padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
      backgroundColor: info.bg, color: info.color, whiteSpace: 'nowrap',
    }}>
      {info.label}
    </span>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────────────────── */
export default function ContactMessagesPage() {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 1, page: 1 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [filters, setFilters] = useState({
    page: 1, limit: 20,
    search: '', statut: '', subject: '',
    startDate: '', endDate: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  // modals
  const [viewItem, setViewItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editForm, setEditForm] = useState({ statut: '', notes_admin: '' });

  const [toast, setToast] = useState({ message: '', type: 'success' });
  const showToast = (message, type = 'success') => setToast({ message, type });
  const hideToast = () => setToast({ message: '', type: 'success' });

  /* ─── Responsive ─────────────────────────────────────────────────────── */
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  /* ─── Fetch list ─────────────────────────────────────────────────────── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: filters.page, limit: filters.limit };
      if (filters.search) params.search = filters.search;
      if (filters.statut) params.statut = filters.statut;
      if (filters.subject) params.subject = filters.subject;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      const { data: res } = await contactMessageService.list(params);
      setData(res.data);
      setPagination(res.pagination || { total: 0, pages: 1, page: 1 });
    } catch (err) {
      showToast(extractError(err), 'danger');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ─── Fetch stats ────────────────────────────────────────────────────── */
  const fetchStats = useCallback(async () => {
    try {
      const { data: res } = await contactMessageService.getStats();
      setStats(res.data);
    } catch (_) { /* stats are non-critical */ }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  /* ─── Helpers ────────────────────────────────────────────────────────── */
  const setF = key => val => setFilters(f => ({ ...f, [key]: val, page: 1 }));

  const hasActiveFilters = filters.search || filters.statut || filters.subject
    || filters.startDate || filters.endDate;

  const clearFilters = () => setFilters(f => ({
    ...f, search: '', statut: '', subject: '', startDate: '', endDate: '', page: 1,
  }));

  /* ─── View modal ─────────────────────────────────────────────────────── */
  const openView = row => setViewItem(row);
  const closeView = () => setViewItem(null);

  /* ─── Edit modal ─────────────────────────────────────────────────────── */
  const openEdit = row => {
    setEditItem(row);
    setEditForm({ statut: row.statut || 'nouveau', notes_admin: row.notes_admin || '' });
  };
  const closeEdit = () => { setEditItem(null); };

  const handleSave = async () => {

    setSaving(true);
    try {
      await contactMessageService.update(editItem.id, {
        statut: editForm.statut,
        notes_admin: editForm.notes_admin || null,
      });
      showToast('Message mis à jour avec succès');
      closeEdit();
      fetchData();
      fetchStats();
    } catch (err) {
      showToast(extractError(err), 'danger');
    } finally {
      setSaving(false);
    }
  };

  /* ─── Delete ─────────────────────────────────────────────────────────── */
  const handleDelete = async () => {
    try {
      await contactMessageService.remove(deleteTarget.id);
      showToast('Message supprimé avec succès');
      setDeleteTarget(null);
      fetchData();
      fetchStats();
    } catch (err) {
      showToast(extractError(err), 'danger');
      setDeleteTarget(null);
    }
  };

  /* ─── Render ─────────────────────────────────────────────────────────── */
  return (
    <div>
      <Toast message={toast.message} type={toast.type} onClose={hideToast} />

      {/* Header */}
      <div className="text-center mb-3">
        <h5 style={{ fontFamily: 'serif', letterSpacing: 3, fontWeight: 400 }}>MESSAGES DE CONTACT</h5>
        <StarDivider lineWidth="5%" />
      </div>

      {/* Stats row */}
      {stats && (
        <Row className="g-3 mb-4">
          <Col xs={6} md={3}>
            <StatCard icon={BsEnvelope} label="Total" value={stats.total} bg="#fce8e8" color="#8B0000" />
          </Col>
          <Col xs={6} md={3}>
            <StatCard icon={BsChatLeftText} label="Nouveaux (Non lu)" value={stats.nouveau} bg="#fce8e8" color="#8B0000" />
          </Col>
          <Col xs={6} md={3}>
            <StatCard icon={BsEnvelopeOpen} label="Lu" value={stats.lu} bg="#fff3cd" color="#856404" />
          </Col>
          <Col xs={6} md={3}>
            <StatCard icon={BsCheckCircle} label="Répondus" value={stats.repondu} bg="#d5f5e3" color="#1e8449" />
          </Col>
        </Row>
      )}

      {/* Filters bar */}
      <div className="bg-white p-3 rounded mb-4" style={{ border: '1px solid #eee' }}>
        {/* Top row: search + toggle */}
        <div className="d-flex flex-wrap gap-2 align-items-end">
          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
            <Form.Label className="small fw-semibold d-block mb-1">Recherche</Form.Label>
            <div style={{ position: 'relative' }}>
              <BsSearch size={12} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#aaa' }} />
              <Form.Control
                size="sm"
                value={filters.search}
                onChange={e => setF('search')(e.target.value)}
                placeholder="Nom, e-mail, message…"
                style={{ paddingLeft: 28 }} />
            </div>
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <Form.Label className="small fw-semibold d-block mb-1">Statut</Form.Label>
            <Form.Select size="sm" value={filters.statut} onChange={e => setF('statut')(e.target.value)}>
              <option value="">Tous les statuts</option>
              {STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Form.Select>
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <Form.Label className="small fw-semibold d-block mb-1">Sujet</Form.Label>
            <Form.Select size="sm" value={filters.subject} onChange={e => setF('subject')(e.target.value)}>
              <option value="">Tous les sujets</option>
              {SUBJECTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Form.Select>
          </div>

          {/* More filters toggle */}
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setShowFilters(v => !v)}
            style={{ whiteSpace: 'nowrap' }}>
            <BsFilter size={13} className="me-1" />
            {showFilters ? 'Moins' : 'Dates'}
          </Button>

          {hasActiveFilters && (
            <Button size="sm" variant="outline-secondary" onClick={clearFilters} style={{ whiteSpace: 'nowrap' }}>
              <BsXCircle size={12} className="me-1" />
              EFFACER
            </Button>
          )}
        </div>

        {/* Date range (collapsible) */}
        {showFilters && (
          <div className="d-flex flex-wrap gap-2 align-items-end mt-3 pt-3" style={{ borderTop: '1px solid #f5f5f5' }}>
            <div style={{ flex: '1 1 160px' }}>
              <Form.Label className="small fw-semibold d-block mb-1">Date début</Form.Label>
              <Form.Control size="sm" type="date" value={filters.startDate} onChange={e => setF('startDate')(e.target.value)} />
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <Form.Label className="small fw-semibold d-block mb-1">Date fin</Form.Label>
              <Form.Control size="sm" type="date" value={filters.endDate} onChange={e => setF('endDate')(e.target.value)} />
            </div>
          </div>
        )}
      </div>

      {/* Result count */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-bold mb-0">Résultats ({pagination.total ?? data.length})</h6>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" style={{ color: '#8B0000' }} /></div>
      ) : data.length === 0 ? (
        <div className="text-center py-5 text-secondary">Aucun message trouvé.</div>
      ) : isMobile ? (
        /* ── Mobile card list ── */
        <div className="d-flex flex-column gap-3">
          {data.map(row => (
            <div key={row.id} className="mobile-card">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.nom}
                  </div>
                  <div style={{ fontSize: 12, color: '#777', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.email}
                  </div>
                </div>
                <StatutBadge statut={row.statut} />
              </div>
              <div className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 11, color: '#888' }}>
                <BsClock size={11} />
                <span>{formatDate(row.created_at)}</span>
              </div>
              <div className="mobile-section mb-2">
                <div className="mobile-section-title">{subjectLabel(row.subject)}</div>
                <div style={{ color: '#444', lineHeight: 1.5, fontSize: 12 }}>
                  {row.message.length > 120 ? row.message.slice(0, 120) + '…' : row.message}
                </div>
              </div>
              {row.telephone && (
                <div style={{ fontSize: 11, color: '#888', display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4 }}>
                  <BsTelephone size={11} />{row.telephone}
                </div>
              )}
              {row.fichier_url && (
                <div style={{ fontSize: 11, color: '#1a5276', display: 'flex', gap: 4, alignItems: 'center', marginBottom: 8 }}>
                  <BsPaperclip size={11} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row.fichier_nom || 'Pièce jointe'}
                  </span>
                </div>
              )}
              <div className="d-flex gap-2 mt-2">
                <button className="card-btn-edit" onClick={() => openView(row)} style={{ borderRadius: '0 0 0 6px' }}>
                  <BsEye size={13} /> Voir
                </button>
                <button className="card-btn-edit" onClick={() => openEdit(row)} style={{ backgroundColor: '#1a5276', borderRadius: 0 }}>
                  <BsPencil size={13} /> Modifier
                </button>
                <button className="card-btn-delete" onClick={() => setDeleteTarget(row)} style={{ borderRadius: '0 0 6px 0' }}>
                  <BsTrash size={13} /> Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ── Desktop table ── */
        <div className="table-mcm-wrap" style={{ overflowX: 'auto' }}>
          <table className="table-mcm">
            <thead>
              <tr>
                {['Nom', 'E-mail', 'Sujet', 'Statut', 'P.J.', 'Date', 'Actions'].map(h => (
                  <th key={h} className="th-mcm">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map(row => (
                <tr key={row.id}>
                  <td className="td-mcm" style={{ fontWeight: 600 }}>{row.nom}</td>
                  <td className="td-mcm" style={{ color: '#555' }}>{row.email}</td>
                  <td className="td-mcm">
                    <span style={{ fontSize: 12, color: '#444' }}>{subjectLabel(row.subject)}</span>
                  </td>
                  <td className="td-mcm"><StatutBadge statut={row.statut} /></td>
                  <td className="td-mcm">
                    {row.fichier_url
                      ? (
                        <a href={row.fichier_url} target="_blank" rel="noreferrer" title={row.fichier_nom || 'Pièce jointe'} style={{ display: 'inline-flex', color: '#1a5276' }}>
                          <BsPaperclip size={14} />
                        </a>
                      )
                      : <span style={{ color: '#ccc', fontSize: 11 }}>—</span>}
                  </td>
                  <td className="td-mcm" style={{ whiteSpace: 'nowrap', color: '#666' }}>{formatDateShort(row.created_at)}</td>
                  <td className="td-mcm">
                    <div className="d-flex gap-2">
                      <button className="icon-btn-mcm" title="Voir le message" onClick={() => openView(row)}>
                        <BsEye size={15} color="#555" />
                      </button>
                      <button className="icon-btn-mcm" title="Modifier le statut" onClick={() => openEdit(row)}>
                        <BsPencil size={15} color="#1a5276" />
                      </button>
                      <button className="icon-btn-mcm" title="Supprimer" onClick={() => setDeleteTarget(row)}>
                        <BsTrash size={15} color="#8B0000" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        currentPage={filters.page}
        totalPages={pagination.pages}
        onPageChange={page => setFilters(f => ({ ...f, page }))}
      />

      {/* ── View Modal ── */}
      <PortalModal show={!!viewItem} onClose={closeView} size="lg">
        <button onClick={closeView} className="modal-close-btn">×</button>
        <h6 style={{ fontWeight: 700, marginBottom: 20, paddingRight: 24 }}>
          Message de — {viewItem?.nom}
        </h6>

        {viewItem && (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
              <StatutBadge statut={viewItem.statut} />
              <span style={{ fontSize: 12, color: '#888' }}>
                <BsClock size={11} className="me-1" />
                {formatDate(viewItem.created_at)}
              </span>
            </div>

            {/* Info grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '12px 24px',
              backgroundColor: '#f7f8fa',
              borderRadius: 8,
              padding: '14px 16px',
              marginBottom: 20,
              fontSize: 13,
            }}>
              <div>
                <span className="mobile-label">Nom : </span>
                <span style={{ fontWeight: 600 }}>{viewItem.nom}</span>
              </div>
              <div>
                <span className="mobile-label">E-mail : </span>
                <a href={`mailto:${viewItem.email}`} style={{ color: '#1a5276' }}>{viewItem.email}</a>
              </div>
              {viewItem.telephone && (
                <div>
                  <span className="mobile-label">Téléphone : </span>
                  <span>{viewItem.telephone}</span>
                </div>
              )}
              <div>
                <span className="mobile-label">Sujet : </span>
                <span>{subjectLabel(viewItem.subject)}</span>
              </div>
            </div>

            {/* Message body */}
            <div style={{ marginBottom: 20 }}>
              <div className="modal-label mb-2">Message :</div>
              <div style={{
                backgroundColor: '#faf9f7',
                border: '1px solid #ece8e8',
                borderRadius: 6,
                padding: '12px 16px',
                fontSize: 13,
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
                maxHeight: 220,
                overflowY: 'auto',
              }}>
                {viewItem.message}
              </div>
            </div>

            {/* Attachment */}
            {viewItem.fichier_url && (
              <div style={{ marginBottom: 20 }}>
                <div className="modal-label mb-2" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <BsPaperclip size={14} />
                  Pièce jointe
                  {viewItem.fichier_taille && (
                    <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>
                      ({(viewItem.fichier_taille / 1024).toFixed(0)} Ko)
                    </span>
                  )}
                </div>
                {viewItem.fichier_type?.startsWith('image/') ? (
                  <div style={{ border: '1px solid #ece8e8', borderRadius: 6, overflow: 'hidden', display: 'block', maxWidth: '100%', width: 'fit-content' }}>
                    <a href={viewItem.fichier_url} target="_blank" rel="noreferrer" title="Ouvrir en plein écran">
                      <img
                        src={viewItem.fichier_url}
                        alt={viewItem.fichier_nom || 'Pièce jointe'}
                        style={{ maxWidth: '100%', width: 200, maxHeight: 200, display: 'block', objectFit: 'contain', cursor: 'pointer' }}
                      />
                    </a>
                    <div style={{
                      padding: '8px 12px', backgroundColor: '#f7f8fa',
                      borderTop: '1px solid #ece8e8', display: 'flex',
                      alignItems: 'center', justifyContent: 'space-between', gap: 8,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <BsFileEarmarkImage size={13} color="#1a5276" />
                        <span style={{ fontSize: 12, color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {viewItem.fichier_nom}
                        </span>
                      </div>
                      <a
                        href={viewItem.fichier_url}
                        download={viewItem.fichier_nom}
                        target="_blank"
                        rel="noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#1a5276', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
                      >
                        <BsDownload size={13} /> Télécharger
                      </a>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    backgroundColor: '#f7f8fa', border: '1px solid #ece8e8',
                    borderRadius: 6, padding: '12px 14px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <BsFileEarmark size={20} color="#1a5276" style={{ flexShrink: 0 }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {viewItem.fichier_nom || 'Fichier joint'}
                        </div>
                        {viewItem.fichier_type && (
                          <div style={{ fontSize: 11, color: '#999' }}>{viewItem.fichier_type}</div>
                        )}
                      </div>
                    </div>
                    <a
                      href={viewItem.fichier_url}
                      download={viewItem.fichier_nom}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#1a5276', textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      <BsDownload size={13} /> Télécharger
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Admin notes */}
            {viewItem.notes_admin && (
              <div>
                <div className="modal-label mb-2">Notes admin :</div>
                <div style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeeba',
                  borderRadius: 6,
                  padding: '10px 14px',
                  fontSize: 12,
                  color: '#856404',
                  whiteSpace: 'pre-wrap',
                }}>
                  {viewItem.notes_admin}
                </div>
              </div>
            )}

            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <button className="btn-primary-mcm" onClick={() => { closeView(); openEdit(viewItem); }}
                style={{ minWidth: 160 }}>
                MODIFIER LE STATUT
              </button>
            </div>
          </>
        )}
      </PortalModal>

      {/* ── Edit Modal ── */}
      <PortalModal show={!!editItem} onClose={closeEdit}>
        <button onClick={closeEdit} className="modal-close-btn">×</button>
        <h6 style={{ fontWeight: 700, marginBottom: 6 }}>Modifier le message</h6>
        {editItem && (
          <p style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>
            De : <strong>{editItem.nom}</strong> — {formatDateShort(editItem.created_at)}
          </p>
        )}

        <Form.Group className="mb-3">
          <Form.Label className="modal-label">Statut :</Form.Label>
          <Form.Select
            value={editForm.statut}
            onChange={e => setEditForm(f => ({ ...f, statut: e.target.value }))}
            style={{ marginTop: 4 }}
          >
            {STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="modal-label">Notes admin :</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={editForm.notes_admin}
            onChange={e => setEditForm(f => ({ ...f, notes_admin: e.target.value }))}
            placeholder="Notes internes (non visibles par l'utilisateur)…"
            style={{ marginTop: 4, fontSize: 13, resize: 'vertical' }}
          />
        </Form.Group>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button
            className="btn-primary-mcm"
            onClick={handleSave}
            disabled={saving}
            style={{ minWidth: 180, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            {saving ? <Spinner size="sm" animation="border" /> : 'ENREGISTRER'}
          </button>
        </div>
      </PortalModal>

      {/* ── Delete Modal ── */}
      <PortalModal show={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%', backgroundColor: '#ffe5e5',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px',
          }}>
            <BsTrash size={22} color="#8B0000" />
          </div>
          <h6 style={{ fontWeight: 700, marginBottom: 2 }}>Supprimer ce message ?</h6>
          <p style={{ fontSize: 13, color: '#777', margin: 0 }}>Cette action est irréversible.</p>
        </div>

        {deleteTarget && (
          <div style={{
            backgroundColor: '#f7f8fa', borderRadius: 8,
            padding: '14px 16px', marginBottom: 24, fontSize: 13,
          }}>
            <div className="mb-1">
              <span className="fw-semibold">{deleteTarget.nom}</span>
              {' '}<span style={{ color: '#888', fontSize: 12 }}>{deleteTarget.email}</span>
            </div>
            <div style={{ color: '#555' }}>
              {deleteTarget.message?.slice(0, 80)}{deleteTarget.message?.length > 80 ? '…' : ''}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button
            onClick={() => setDeleteTarget(null)}
            style={{ padding: '8px 24px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 }}
          >
            ANNULER
          </button>
          <button
            onClick={handleDelete}
            style={{ padding: '8px 24px', borderRadius: 4, border: 'none', backgroundColor: '#8B0000', color: '#fff', cursor: 'pointer', fontSize: 13 }}
          >
            SUPPRIMER
          </button>
        </div>
      </PortalModal>
    </div>
  );
}
